import { useRef, useState } from 'react';
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { ExternalLink, Clock, Layers, PenTool } from 'lucide-react';
import { GalleryLightbox } from './GalleryLightbox';

const HERO_IMAGE = 'https://static.wixstatic.com/media/ef739d_fa37be2d451a44b784f20b032cf6d3def000.jpg/v1/fill/w_848,h_477,al_c,q_85,usm_0.33_1.00_0.00,enc_avif,quality_auto/ef739d_fa37be2d451a44b784f20b032cf6d3def000.jpg';

const GALLERY_IMAGES = [
  'https://customer-assets.emergentagent.com/job_ghost-gallery-2/artifacts/rowpi7nj_IMG_20231213_212248_Original.JPG',
  'https://customer-assets.emergentagent.com/job_ghost-gallery-2/artifacts/n0a6lmzo_IMG_20231213_212408_Original.JPG',
  'https://customer-assets.emergentagent.com/job_ghost-gallery-2/artifacts/gotjt5vl_IMG_20231213_212430_Original.JPG',
  'https://customer-assets.emergentagent.com/job_ghost-gallery-2/artifacts/ibyfhvjs_IMG_20231213_212420_Original.JPG',
  'https://customer-assets.emergentagent.com/job_ghost-gallery-2/artifacts/rz8fz26t_IMG_20231213_212347_Original.JPG',
  'https://customer-assets.emergentagent.com/job_ghost-gallery-2/artifacts/3gk43fl1_IMG_20231213_212329_Original.JPG',
  'https://customer-assets.emergentagent.com/job_ghost-gallery-2/artifacts/3703xm0d_IMG_20231213_212202_Original.JPG',
  'https://customer-assets.emergentagent.com/job_ghost-gallery-2/artifacts/j20luf3s_IMG_20231213_212310_Original.JPG',
];

// Format images as lightbox-compatible records — factual fields only, no fabricated provenance
const NAKAMOTO_ARTWORKS = GALLERY_IMAGES.map((img, i) => ({
  id: `NAK-${String(i + 1).padStart(2, '0')}`,
  img,
  mediaType: 'image',
  title: 'Nakamoto Timepiece',
  year: '2022',
  type: 'PHYSICAL / LIMITED EDITION',
  status: 'ARCHIVED',
  description: 'Franck Muller × Zato. Edition of 100.',
}));

// Symmetric offsets so all images sit at Y=0 when section is centred in viewport
const PARALLAX_OFFSETS = [
  [-12, 12],
  [-22, 22],
  [-8,  8],
  [-18, 18],
  [-28, 28],
  [-10, 10],
  [-20, 20],
  [-16, 16],
];

const specs = [
  { icon: <Layers size={14} />, label: 'EDITION', value: '100 PIECES' },
  { icon: <Clock size={14} />, label: 'YEAR', value: '2022' },
  { icon: <PenTool size={14} />, label: 'CONCEPT + DESIGN', value: 'ZATO' },
  { icon: <ExternalLink size={14} />, label: 'MAKER', value: 'FRANCK MULLER' },
];

// Separate component so each item can call useTransform at the top level
function NakamotoGalleryItem({ src, index, scrollYProgress, onClick }) {
  const offsets = PARALLAX_OFFSETS[index] || [-10, 10];
  const y = useTransform(scrollYProgress, [0, 1], offsets);

  return (
    <motion.div
      className="nakamoto-gallery-item"
      whileHover={{ scale: 1.03, zIndex: 10 }}
      transition={{ duration: 0.2 }}
      onClick={() => onClick(index)}
      style={{ cursor: 'pointer' }}
      data-testid={`nakamoto-gallery-item-${index}`}
    >
      <motion.img
        src={src}
        alt={`Nakamoto timepiece view ${index + 1}`}
        className="nakamoto-gallery-img"
        style={{ y }}
        loading="lazy"
      />
      <div className="nakamoto-gallery-overlay">
        <span className="font-mono text-xs text-[#FFC700]">VIEW {String(index + 1).padStart(2, '0')}</span>
      </div>
    </motion.div>
  );
}

function NakamotoHeader({ isInView }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7 }}
      className="mb-16"
    >
      <span className="mono-tag text-[#FFC700]">// SECTION_02 :: ARCHIVE</span>
      <h2 className="section-title mt-4">
        NAKAMOTO
      </h2>
      <p className="body-text text-white/40 mt-4 max-w-md font-mono text-sm">
        FRANCK MULLER × ZATO | 2022 | EDITION 100
      </p>
    </motion.div>
  );
}

function NakamotoHeroVideo({ isInView }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.8, delay: 0.2 }}
      className="nakamoto-hero-img-container mb-8"
      data-testid="nakamoto-hero-image"
    >
      <video
        src="/nakamoto-video.mp4"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        poster={HERO_IMAGE}
        className="nakamoto-hero-img"
        data-testid="nakamoto-hero-video"
      />
      <div className="nakamoto-img-overlay" />
    </motion.div>
  );
}

function NakamotoInfo({ isInView }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -30 }}
      animate={isInView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.7, delay: 0.3 }}
    >
      <p className="body-text leading-relaxed text-white/90" data-testid="nakamoto-description">
        In 2022, ZATO conceived and designed Nakamoto, a limited-edition collaboration with Swiss watchmaker Franck Muller.
      </p>
      <p className="body-text text-white/70 leading-relaxed mt-4">
        The project brought together mechanical time and decentralised money. Named after Satoshi Nakamoto, the pseudonymous creator of Bitcoin, the watch translated an idea from the digital monetary world into a physical object.
      </p>
      <p className="body-text text-white/70 leading-relaxed mt-4">
        100 pieces were produced.
      </p>
      <a
        href="https://franckmullerencrypto.com/product/franck-muller-mens-45mm-carbon-fiber-nakamoto/"
        target="_blank"
        rel="noopener noreferrer"
        className="btn-primary mt-8 inline-flex items-center gap-2"
        data-testid="nakamoto-external-link"
      >
        <ExternalLink size={14} />
        FRANCKMULLERENCRYPTO.COM
      </a>
    </motion.div>
  );
}

function NakamotoSpecs({ isInView }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={isInView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.7, delay: 0.4 }}
      className="spec-grid"
      data-testid="nakamoto-specs"
    >
      {specs.map((s) => (
        <div key={s.label} className="spec-cell">
          <div className="spec-icon text-[#FFC700]">{s.icon}</div>
          <div className="spec-label">{s.label}</div>
          <div className="spec-value">{s.value}</div>
        </div>
      ))}
    </motion.div>
  );
}

function NakamotoGalleryGrid({ isInView, scrollYProgress, onOpen }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: 0.5 }}
      className="nakamoto-gallery"
      data-testid="nakamoto-gallery"
    >
      {NAKAMOTO_ARTWORKS.map((artwork, i) => (
        <NakamotoGalleryItem
          key={artwork.id}
          src={artwork.img}
          index={i}
          scrollYProgress={scrollYProgress}
          onClick={onOpen}
        />
      ))}
    </motion.div>
  );
}

export function NakamotoSection() {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-80px' });

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  const handleClose = () => setLightboxIndex(null);
  const handleNav = (delta) => setLightboxIndex((prev) => (prev + delta + NAKAMOTO_ARTWORKS.length) % NAKAMOTO_ARTWORKS.length);

  return (
    <section id="nakamoto" className="py-32 px-6 relative overflow-hidden" data-testid="nakamoto-section" ref={sectionRef}>
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#FFC700]/40 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#050505]/20 to-transparent pointer-events-none" />

      <div className="max-w-7xl mx-auto">
        <NakamotoHeader isInView={isInView} />
        <NakamotoHeroVideo isInView={isInView} />

        <div className="grid md:grid-cols-2 gap-12 mb-16">
          <NakamotoInfo isInView={isInView} />
          <NakamotoSpecs isInView={isInView} />
        </div>

        <NakamotoGalleryGrid isInView={isInView} scrollYProgress={scrollYProgress} onOpen={setLightboxIndex} />
      </div>

      <AnimatePresence>
        {lightboxIndex !== null && (
          <GalleryLightbox
            artworks={NAKAMOTO_ARTWORKS}
            activeIndex={lightboxIndex}
            onClose={handleClose}
            onNav={handleNav}
          />
        )}
      </AnimatePresence>
    </section>
  );
}
